import React, { useState } from "react";
import profileIcon from "../assets/profile-user.webp";
import { useUser } from "../context/AuthContext";
import { updateUserName, changeUserName, changeUserPassword } from "../api";
import ParticleBackground from "../components/ParticleBackground";

const brand = {
  nearBlack: '#141318',
  darkGray: '#1F2937',
  light: '#E9EAE8',
  white: '#FFFFFF',
  blueGray: '#566D87',
  gold: '#FFD404',
  ink: '#00181C',
};

const Profile = () => {
  const { user, verificarAutenticacion } = useUser();
  const [showEditName, setShowEditName] = useState(false);
  const [showChangePass, setShowChangePass] = useState(false);
  const [newName, setNewName] = useState(user?.name || "");
  const [nameError, setNameError] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [passForm, setPassForm] = useState({ current: "", newPass: "", confirm: "" });
  const [passError, setPassError] = useState("");
  const [passSuccess, setPassSuccess] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  const handleNameSave = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      setNameError("Name cannot be empty");
      return;
    }
    setNameError("");
    setNameLoading(true);
    try {
      if (user.role === "admin") {
        await updateUserName(user.id, newName.trim());
      } else {
        await changeUserName(newName.trim());
      }
      setShowEditName(false);
      setNameLoading(false);
      await verificarAutenticacion(); // Refresca el usuario en contexto
    } catch (err) {
      setNameLoading(false);
      setNameError(
        err.response?.data?.message || err.message || "Error updating name"
      );
    }
  };

  const handlePassSave = async (e) => {
    e.preventDefault();
    setPassError("");
    setPassSuccess("");
    if (!passForm.current || !passForm.newPass || !passForm.confirm) {
      setPassError("All fields are required");
      return;
    }
    if (passForm.newPass.length < 6) {
      setPassError("New password must be at least 6 characters");
      return;
    }
    if (passForm.newPass !== passForm.confirm) {
      setPassError("Passwords do not match");
      return;
    }
    setPassLoading(true);
    try {
      await changeUserPassword(passForm.current, passForm.newPass);
      setPassSuccess("Password changed successfully");
      setShowChangePass(false);
      setPassForm({ current: "", newPass: "", confirm: "" });
    } catch (err) {
      setPassError(
        err.response?.data?.message || err.message || "Error changing password"
      );
    } finally {
      setPassLoading(false);
    }
  };

  return (
    <>
  <div className="relative min-h-screen flex flex-col items-center justify-center py-8 sm:py-12 md:py-16 overflow-x-hidden" style={{ background: `radial-gradient(1000px 500px at 10% -10%, rgba(255, 212, 4, 0.06), transparent 70%), radial-gradient(900px 500px at 110% 10%, rgba(86, 109, 135, 0.06), transparent 70%), ${brand.nearBlack}`, color: brand.light }}>
      {/* Fondo decorativo con "luces" + partículas */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ParticleBackground className="opacity-25" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-70 animate-pulse" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(255,212,4,0.18), transparent 60%)' }} />
        <div className="absolute bottom-0 right-0 w-[320px] h-[220px] rounded-full blur-2xl opacity-60 animate-pulse" style={{ background: 'radial-gradient(circle at 70% 70%, rgba(86,109,135,0.18), transparent 60%)' }} />
      </div>

      <div className="relative z-10 w-full max-w-lg px-2 sm:px-4 md:px-0">
        <div className="rounded-3xl shadow-2xl px-4 sm:px-8 md:px-10 py-6 sm:py-10 md:py-12 flex flex-col items-center backdrop-blur-xl animate-fade-in" style={{ backgroundColor: 'rgba(31,41,55,0.55)', border: `1px solid ${brand.blueGray}33` }}>
          {/* Avatar grande */}
          <div className="-mt-20 sm:-mt-24 mb-4 w-24 h-24 sm:w-36 sm:h-36 rounded-full bg-gradient-to-br flex items-center justify-center shadow-xl" style={{ backgroundImage: `linear-gradient(to bottom right, ${brand.gold}, ${brand.blueGray})`, border: `4px solid ${brand.darkGray}` }}>
            <img src={profileIcon} alt="Profile" className="w-20 h-20 sm:w-32 sm:h-32 rounded-full object-cover" />
          </div>
          {/* Nombre y saludo */}
          <h1
            className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-1 tracking-tight text-center"
            style={{
              fontFamily: 'Inter, Audiowide, sans-serif',
              color: brand.white,
              textShadow: '0 2px 12px rgba(0,0,0,0.5)',
            }}
          >
            Welcome, {user?.name || '-'}
          </h1>
          <p
            className="text-base sm:text-lg mb-6 font-medium text-center"
            style={{
              color: brand.blueGray,
              textShadow: '0 2px 8px rgba(0,0,0,0.45)',
            }}
          >
            This is your profile page
          </p>
          {/* Datos destacados */}
          <div className="w-full flex flex-col gap-4 sm:gap-6 mt-2">
            {/* Nombre editable */}
            <div className="flex items-center justify-between rounded-xl px-4 sm:px-5 py-4 w-full" style={{ backgroundColor: 'rgba(20,19,24,0.8)', border: `1px solid ${brand.blueGray}33` }}>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-sm" style={{ color: brand.blueGray }}>Full Name</span>
                <span className="text-lg font-semibold" style={{ color: brand.white, textShadow:'0 1px 6px rgba(0,0,0,0.4)' }}>{user?.name || '-'}</span>
              </div>
              <button
                className="px-4 py-2 rounded-lg font-semibold text-sm shadow transition hover:opacity-90"
                style={{ backgroundColor: brand.gold, color: brand.ink }}
                onClick={() => { setShowEditName(true); setNewName(user?.name || ""); setNameError(""); }}
              >
                Edit
              </button>
            </div>
            
            {/* Email (no editable) */}
            <div className="flex items-center justify-between rounded-xl px-4 sm:px-5 py-4 w-full" style={{ backgroundColor: 'rgba(20,19,24,0.8)', border: `1px solid ${brand.blueGray}33` }}>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-sm" style={{ color: brand.blueGray }}>Email</span>
                <span className="text-lg font-semibold" style={{ color: brand.white, textShadow:'0 1px 6px rgba(0,0,0,0.4)' }}>{user?.email || '-'}</span>
              </div>
            </div>
            
            {/* Cambiar contraseña */}
            <div className="flex items-center justify-between rounded-xl px-4 sm:px-5 py-4 w-full" style={{ backgroundColor: 'rgba(20,19,24,0.8)', border: `1px solid ${brand.blueGray}33` }}>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-sm" style={{ color: brand.blueGray }}>Password</span>
                <span className="text-lg font-semibold" style={{ color: brand.white, textShadow:'0 1px 6px rgba(0,0,0,0.4)' }}>••••••••</span>
              </div>
              <button
                className="px-4 py-2 rounded-lg font-semibold text-sm shadow transition hover:opacity-90"
                style={{ backgroundColor: brand.gold, color: brand.ink }}
                onClick={() => { setShowChangePass(true); setPassError(""); setPassSuccess(""); setPassForm({ current: "", newPass: "", confirm: "" }); }}
              >
                Change
              </button>
            </div>
          </div>
          {/* Línea decorativa */}
          <div className="w-full h-[1px] my-6 sm:my-8 bg-gradient-to-r from-transparent via-[#FFD404] to-transparent opacity-80" />
        </div>
      </div>
    </div>

      {/* Modales completamente fuera del contenedor principal */}
      {showEditName && (
        <>
          {/* Overlay que cubre toda la pantalla */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 9999,
            }}
            onClick={() => setShowEditName(false)}
          />
          
          {/* Modal centrado en la pantalla */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
              maxWidth: '90vw',
              width: '400px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <form
              className="rounded-2xl shadow-2xl p-4 sm:p-8 w-full flex flex-col gap-4 animate-fade-in"
              style={{ backgroundColor: brand.nearBlack, border: `1px solid ${brand.blueGray}55` }}
              onSubmit={handleNameSave}
            >
              <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ color: brand.blueGray }}>Edit Name</h2>
              <input
                className="w-full px-3 py-2 rounded-lg text-white text-base focus:outline-none focus:ring-2"
                style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, boxShadow: 'none' }}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Enter new name"
                autoFocus
              />
              {nameError && <div className="text-red-400 text-sm">{nameError}</div>}
              <div className="flex gap-2 mt-2">
                <button type="button" className="flex-1 py-2 rounded-lg bg-gray-600 text-white font-semibold hover:bg-gray-700 transition" onClick={() => setShowEditName(false)}>Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded-lg font-bold transition" style={{ backgroundColor: brand.gold, color: brand.ink }} disabled={nameLoading}>
                  {nameLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {showChangePass && (
        <>
          {/* Overlay que cubre toda la pantalla */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 9999,
            }}
            onClick={() => setShowChangePass(false)}
          />
          
          {/* Modal centrado en la pantalla */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
              maxWidth: '90vw',
              width: '400px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <form
              className="rounded-2xl shadow-2xl p-4 sm:p-8 w-full flex flex-col gap-4 animate-fade-in"
              style={{ backgroundColor: brand.nearBlack, border: `1px solid ${brand.blueGray}55` }}
              onSubmit={handlePassSave}
            >
              <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ color: brand.blueGray }}>Change Password</h2>
              <input
                className="w-full px-3 py-2 rounded-lg text-white text-base focus:outline-none focus:ring-2"
                style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55` }}
                type="password"
                value={passForm.current}
                onChange={e => setPassForm(f => ({ ...f, current: e.target.value }))}
                placeholder="Current password"
                autoFocus
              />
              <input
                className="w-full px-3 py-2 rounded-lg text-white text-base focus:outline-none focus:ring-2"
                style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55` }}
                type="password"
                value={passForm.newPass}
                onChange={e => setPassForm(f => ({ ...f, newPass: e.target.value }))}
                placeholder="New password"
              />
              <input
                className="w-full px-3 py-2 rounded-lg text-white text-base focus:outline-none focus:ring-2"
                style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55` }}
                type="password"
                value={passForm.confirm}
                onChange={e => setPassForm(f => ({ ...f, confirm: e.target.value }))}
                placeholder="Confirm new password"
              />
              {passError && <div className="text-red-400 text-sm">{passError}</div>}
              {passSuccess && <div className="text-green-400 text-sm">{passSuccess}</div>}
              <div className="flex gap-2 mt-2">
                <button type="button" className="flex-1 py-2 rounded-lg bg-gray-600 text-white font-semibold hover:bg-gray-700 transition" onClick={() => setShowChangePass(false)}>Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded-lg font-bold transition" style={{ backgroundColor: brand.gold, color: brand.ink }} disabled={passLoading}>
                  {passLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default Profile;
